import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ExclamationCircleIcon, LockClosedIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';

interface PublicVariation {
  id: string;
  size?: string | null;
  color?: string | null;
  price: number;
  inventory: number;
}

interface PublicProduct {
  id: string;
  name: string;
  description: string;
  images: string[];
  studio_name: string | null;
  payments_enabled: boolean;
  variations: PublicVariation[];
}

const variationLabel = (v: PublicVariation) => {
  const parts = [v.size, v.color].filter(Boolean);
  return parts.length ? parts.join(' / ') : 'Standard';
};

export default function PublicProductPurchase() {
  const { productId } = useParams<{ productId: string }>();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const [product, setProduct] = useState<PublicProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    fetch(`${backendUrl}/api/stripe/public/products/${productId}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Product not found');
        setProduct(data);
        const inStock = (data.variations as PublicVariation[]).filter((v) => v.inventory > 0);
        if (inStock.length === 1) setSelectedId(inStock[0].id);
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Product not found'))
      .finally(() => setLoading(false));
  }, [productId, backendUrl]);

  const selected = useMemo(
    () => product?.variations.find((v) => v.id === selectedId) ?? null,
    [product, selectedId]
  );
  const maxQty = Math.min(selected?.inventory ?? 0, 100);
  const total = selected ? selected.price * quantity : 0;
  const prices = product?.variations.map((v) => v.price) ?? [];
  const minPrice = prices.length ? Math.min(...prices) : 0;
  const maxPrice = prices.length ? Math.max(...prices) : 0;
  const soldOut = !!product && product.variations.every((v) => v.inventory <= 0);

  const handleCheckout = async () => {
    if (!product || !selected) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${backendUrl}/api/stripe/public/products/${product.id}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variation_id: selected.id, quantity }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.url) throw new Error(data.error || 'Could not start checkout');
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start checkout');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  if (loadError || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center">
          <ShoppingBagIcon className="mx-auto h-12 w-12 text-gray-300" />
          <h1 className="mt-4 text-lg font-semibold text-gray-900">This product isn't available</h1>
          <p className="mt-1 text-sm text-gray-500">The link may be out of date. Please contact the studio.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12 px-4">
      <div className="mx-auto max-w-3xl">
        {product.studio_name && (
          <p className="mb-4 text-center text-sm font-medium text-gray-500">{product.studio_name}</p>
        )}

        <div className="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-gray-200 md:grid md:grid-cols-2">
          {/* Images */}
          <div className="bg-gray-100">
            {product.images.length > 0 ? (
              <>
                <img
                  src={product.images[activeImage]}
                  alt={product.name}
                  className="aspect-square w-full object-cover"
                />
                {product.images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto p-3">
                    {product.images.map((img, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveImage(i)}
                        className={`h-14 w-14 flex-shrink-0 overflow-hidden rounded-md ring-2 ${
                          i === activeImage ? 'ring-indigo-500' : 'ring-transparent'
                        }`}
                      >
                        <img src={img} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="flex aspect-square items-center justify-center">
                <ShoppingBagIcon className="h-16 w-16 text-gray-300" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-col p-6">
            <h1 className="text-2xl font-semibold text-gray-900">{product.name}</h1>
            <p className="mt-1 text-lg text-gray-700">
              {selected
                ? `$${selected.price.toFixed(2)}`
                : minPrice === maxPrice
                ? `$${minPrice.toFixed(2)}`
                : `$${minPrice.toFixed(2)} – $${maxPrice.toFixed(2)}`}
            </p>
            {product.description && (
              <p className="mt-4 whitespace-pre-line text-sm text-gray-600">{product.description}</p>
            )}

            {soldOut ? (
              <p className="mt-6 rounded-md bg-gray-100 px-4 py-3 text-center text-sm font-medium text-gray-600">
                Sold out
              </p>
            ) : (
              <div className="mt-6 space-y-5">
                {product.variations.length > 1 && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Option</label>
                    <div className="grid grid-cols-2 gap-2">
                      {product.variations.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          disabled={v.inventory <= 0}
                          onClick={() => {
                            setSelectedId(v.id);
                            setQuantity((q) => Math.max(1, Math.min(q, v.inventory)));
                          }}
                          className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                            selectedId === v.id
                              ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="font-medium text-gray-900">{variationLabel(v)}</div>
                          <div className="text-xs text-gray-500">
                            {v.inventory > 0 ? `$${v.price.toFixed(2)}` : 'Sold out'}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {selected && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Quantity</label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        className="flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-lg text-gray-700 hover:bg-gray-50"
                      >
                        −
                      </button>
                      <span className="w-8 text-center text-sm font-medium text-gray-900">{quantity}</span>
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                        disabled={quantity >= maxQty}
                        className="flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 text-lg text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                    <ExclamationCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={!selected || submitting || !product.payments_enabled}
                  className="w-full rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting
                    ? 'Redirecting…'
                    : !product.payments_enabled
                    ? 'Online payments unavailable'
                    : selected
                    ? `Pay $${total.toFixed(2)}`
                    : 'Choose an option'}
                </button>
                <p className="flex items-center justify-center gap-1 text-xs text-gray-400">
                  <LockClosedIcon className="h-3.5 w-3.5" />
                  Secure checkout powered by Stripe
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
